/**
 * EhrTasksReportPage — the EHR Tasks report: every EHR task currently waiting for review at
 * a community, grouped the way the EHR Tasks page groups them (docs/EHR_TASKS_REPORT_PLAN.md).
 *
 * A "right now" report like Maintenance: no range or window. It runs as soon as it opens,
 * on a campus change and on a filter change, and is not cached (useReportRestore): the list
 * goes stale the moment someone approves a task, and the query is two small procs.
 *
 * Gating (plan section 2): Reports.jsx only mounts this when canSeeEhrTasksReport() holds,
 * and the campus selector lists only campuses with an active EHR integration. Which
 * sections exist comes from the Lambda, which reads the integration flags itself.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import HeaderInfoTip from '../../../components/v2/HeaderInfoTip.jsx';
import {
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
    getCampusConfig,
    getCampusTzOffset,
    getEhrInfo,
} from '../../../services/session.js';
import { exportEhrTasksReportCsv, exportEhrTasksReportPdf } from '../../../services/csvExport.js';
import { fetchEhrTasksReport } from './api/ehrTasksReportApi.js';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';
import { fetchPortalFilters } from '../../../utils/reportFilterOptions.js';
import ReportFilterDrawer from '../alert-details/components/ReportFilterDrawer.jsx';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import EhrTasksSection from './components/EhrTasksSection.jsx';
import { EHR_SECTIONS, ehrBucket, formatAsOf } from './ehrTasksColumns.js';
import { ehrTaskTypeOptions } from '../../../utils/ehrTaskTypes.js';
import { EHR_TASKS_REPORT_SECTIONS } from '../../../components/v2/reportFilterSections.js';
import { REPORT_TYPE_ID } from '../../../utils/reportTypes.js';
import { buildFilterChips, countActiveFilters } from '../../../utils/reportFilterChips.js';
import { reportErrorMessage } from '../../../utils/reportPageState.js';

import '../alert-details/alert-details-page.css';
import './ehr-tasks-report-page.css';

/* Searched across every section: the identifying text a person would type. */
const SEARCH_KEYS = ['name', 'nickname', 'area_name', 'old_area_name', 'email', 'username', 'title'];

/** Only the keys this page knows, from Run Report Local's stored filters. */
function initialFilters(navState) {
    const stored = navState?.filters?.taskTypes;
    return { taskTypes: Array.isArray(stored) ? stored.map(String) : [] };
}

const EhrTasksInfoTip = () => (
    <HeaderInfoTip
        spaced
        maxWidth={320}
        title="About this report"
        body="Every EHR task waiting for review at this community right now, oldest first. Approve or reject them on the EHR Tasks page."
        sections={[
            { heading: 'Scheduled move-ins', body: 'Move-ins booked for a future date are not listed. They appear once they can be approved.' },
            { heading: 'Resident info updates', body: 'Changes to resident details are approved automatically, so they are not listed.' },
        ]}
    />
);

const EhrTasksReportPage = () => {
    // -- Session --
    /* Only campuses with an active EHR integration can be picked (plan section 2). */
    const campusList = useMemo(() => (getCampusList() || []).filter((c) => getEhrInfo(c.campus_id).active), []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const tzOffset = getCampusTzOffset(campusId);

    const location = useLocation();
    const navStateRef = useRef(location.state);

    // -- Report state --
    const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
    const [report, setReport] = useState(null);
    const [reportFilters, setReportFilters] = useState(() => initialFilters(navStateRef.current));
    const [search, setSearch] = useState('');
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);
    const [recipientUsers, setRecipientUsers] = useState([]);
    const runIdRef = useRef(0);

    const { toast, showToast, dismissToast } = useToast();

    /* The Task Types this campus's integration sends (residents / users flags). */
    const taskTypeOptions = useMemo(() => ehrTaskTypeOptions(campusId), [campusId]);
    const filterOptions = useMemo(
        () => ({ ehrTaskTypes: taskTypeOptions, users: recipientUsers }),
        [taskTypeOptions, recipientUsers],
    );

    // ======================================================================
    // Run
    // ======================================================================
    const runReport = useCallback(async () => {
        const runId = ++runIdRef.current;
        setStatus('loading');
        const result = await fetchEhrTasksReport({ campusId, taskTypes: reportFilters.taskTypes });
        if (runId !== runIdRef.current) return; // a newer run (campus / filter change) won
        if (result.ErrorCode) {
            showToast('error', reportErrorMessage(result.ErrorCode));
            setStatus('error');
            return;
        }
        setReport(result);
        setStatus('ready');
    }, [campusId, reportFilters, showToast]);

    useEffect(() => { runReport(); }, [runReport]);

    useEffect(() => {
        if (!campusId) return;
        fetchFrequencies().then((d) => setFrequencies(Array.isArray(d) ? d : []));
        /* User Recipients for the schedule drawer; the only filter list this report needs. */
        fetchPortalFilters(campusId, ['users']).then((o) => setRecipientUsers(o.users || []));
    }, [campusId]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const newId = parseInt(e.target.value, 10);
        setCampusId(newId);
        updateSessionCampus(newId);
        setSearch('');
        /* Task types are the same keys on every campus, so the selection carries over;
           a type the new campus does not receive is dropped by the Lambda. */
    }, []);

    const handleApplyFilters = useCallback((filters) => {
        setReportFilters({ taskTypes: (filters.taskTypes || []).map(String) });
        setFilterDrawerOpen(false);
    }, []);

    const handleClearReportFilters = useCallback(() => {
        setReportFilters({ taskTypes: [] });
    }, []);

    const handleScheduleSave = useCallback(async (payload) => {
        const result = await createSchedule(payload, { campusId, userId: getUserId(), clientId: getSessionId() });
        if (result[0]) { showToast('success', 'Schedule created successfully.'); setScheduleDrawerOpen(false); }
        else { showToast('error', result[1] || 'Failed to create schedule.'); }
    }, [campusId, showToast]);

    // ======================================================================
    // Derived
    // ======================================================================
    const includedBuckets = useMemo(
        () => (report?.buckets || []).map(ehrBucket).filter(Boolean),
        [report],
    );

    const searchedRecords = useMemo(() => {
        const records = report?.records || [];
        const term = search.trim().toLowerCase();
        if (!term) return records;
        return records.filter((r) => SEARCH_KEYS.some((k) => String(r[k] || '').toLowerCase().includes(term)));
    }, [report, search]);

    const rowsByBucket = useMemo(() => {
        const map = {};
        includedBuckets.forEach((b) => { map[b.key] = []; });
        searchedRecords.forEach((r) => { if (map[r.task_type]) map[r.task_type].push(r); });
        return map;
    }, [includedBuckets, searchedRecords]);

    const counts = report?.counts || {};
    const totalWaiting = includedBuckets.reduce((n, b) => n + (counts[b.key] || 0), 0);
    const asOfLabel = formatAsOf(report?.asOfUtc, tzOffset);
    const bothSectionsOff = Boolean(report && !report.inactive && !report.flags?.receiving_resident && !report.flags?.receiving_user);

    const reportFilterChips = useMemo(
        () => buildFilterChips(reportFilters, filterOptions, EHR_TASKS_REPORT_SECTIONS),
        [reportFilters, filterOptions],
    );
    const activeReportFilterCount = useMemo(() => countActiveFilters(reportFilters, EHR_TASKS_REPORT_SECTIONS), [reportFilters]);

    // -- Export: what is on screen (search applied), minus the page-only columns --
    const handleExportCsv = useCallback(() => {
        exportEhrTasksReportCsv(searchedRecords, tzOffset, 'ehr-tasks-report.csv');
    }, [searchedRecords, tzOffset]);
    const handleExportPdf = useCallback(() => {
        exportEhrTasksReportPdf(searchedRecords, includedBuckets.map((b) => b.key), tzOffset,
            `EHR Tasks: ${campusConfig?.name || ''}`, asOfLabel ? `As of ${asOfLabel}` : '', 'ehr-tasks-report.pdf');
    }, [searchedRecords, includedBuckets, tzOffset, campusConfig, asOfLabel]);

    const scrollToBucket = (key) => {
        document.getElementById(`etr-bucket-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // ======================================================================
    // Render
    // ======================================================================
    const renderBody = () => {
        if (status === 'loading' && !report) {
            return (
                <div className="ar-loading">
                    <div className="ar-loading__spinner" />
                    <p>Loading EHR tasks...</p>
                </div>
            );
        }
        if (status === 'error' && !report) {
            return (
                <div className="etr-message">
                    <p>The report could not be loaded.</p>
                    <button type="button" className="dp-btn dp-btn--primary dp-btn--sm" onClick={runReport}>Try again</button>
                </div>
            );
        }
        if (report?.inactive) {
            return <div className="etr-message"><p>The EHR integration is no longer active for this community.</p></div>;
        }
        if (bothSectionsOff) {
            return <div className="etr-message"><p>This community&apos;s EHR integration does not send residents or users to PalCare.</p></div>;
        }
        if (includedBuckets.length === 0) {
            return (
                <div className="etr-message">
                    <p>None of the selected task types are received from this community&apos;s EHR integration.</p>
                    <button type="button" className="dp-btn dp-btn--secondary dp-btn--sm" onClick={handleClearReportFilters}>Show all task types</button>
                </div>
            );
        }
        return (
            <>
                {/* Summary: the total, then one card per bucket (click to jump to it). */}
                <div className="etr-stats" role="list">
                    <div className="ar-summary-card etr-stat etr-stat--total" role="listitem">
                        <div className="ar-summary-card__value">{totalWaiting}</div>
                        <div className="ar-summary-card__label">Waiting for review</div>
                    </div>
                    {includedBuckets.map((b) => (
                        <button key={b.key} type="button" role="listitem" className="ar-summary-card etr-stat" onClick={() => scrollToBucket(b.key)}>
                            <div className="ar-summary-card__value">{counts[b.key] || 0}</div>
                            <div className="ar-summary-card__label">{b.title}</div>
                        </button>
                    ))}
                </div>

                {totalWaiting === 0 ? (
                    <div className="etr-message etr-message--done">
                        <i className="fe fe-check-circle etr-message__icon" aria-hidden="true" />
                        <p><strong>You&apos;re all caught up.</strong> No EHR tasks are waiting for review.</p>
                    </div>
                ) : (
                    <>
                        <div className="ap-controls">
                            <div className="ap-controls__left">
                                <div className="ap-search">
                                    <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                    </svg>
                                    <input
                                        type="search"
                                        placeholder="Search tasks..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="ap-search__input"
                                        aria-label="Search EHR tasks"
                                    />
                                </div>
                            </div>
                            <div className="ap-controls__right">
                                <button type="button" className="rp-btn rp-btn--secondary" onClick={handleExportCsv}>
                                    <i className="fe fe-download" style={{ marginRight: '4px' }} />CSV
                                </button>
                                <button type="button" className="rp-btn rp-btn--secondary" onClick={handleExportPdf}>
                                    <i className="fe fe-file-text" style={{ marginRight: '4px' }} />PDF
                                </button>
                            </div>
                        </div>

                        {EHR_SECTIONS.map((section) => {
                            const buckets = includedBuckets.filter((b) => b.section === section.key);
                            if (buckets.length === 0) return null;
                            return (
                                <div key={section.key} className="etr-section">
                                    <h2 className="etr-section__title">{section.title}</h2>
                                    {buckets.map((b) => (
                                        <EhrTasksSection key={b.key} bucket={b} rows={rowsByBucket[b.key] || []} tzOffset={tzOffset} />
                                    ))}
                                </div>
                            );
                        })}
                    </>
                )}
            </>
        );
    };

    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ps-page">
                    <Toast toast={toast} onDismiss={dismissToast} />

                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <div className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ps-breadcrumb__current">EHR Tasks</span>
                                </div>
                                <div className="ps-campus-row">
                                    <label htmlFor="etr-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="etr-campus-selector" value={campusId} onChange={handleCampusChange} className="ps-campus-select">
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ps-page-title">EHR Tasks<EhrTasksInfoTip /></h1>
                                </div>
                                <div className="ar-title-row__actions">
                                    {asOfLabel ? (
                                        <span className="ar-time-summary etr-as-of" title="Community time">
                                            <i className="fe fe-clock" aria-hidden="true" />
                                            As of {asOfLabel}
                                        </span>
                                    ) : null}
                                    <button type="button" className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setScheduleDrawerOpen(true)}>
                                        <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                    </button>
                                    <button type="button" className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setFilterDrawerOpen(true)} style={{ position: 'relative' }}>
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeReportFilterCount > 0 ? <span className="ar-filter-badge">{activeReportFilterCount}</span> : null}
                                    </button>
                                    <button type="button" className="dp-btn dp-btn--primary dp-btn--sm" onClick={runReport} disabled={status === 'loading'}>
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Refreshing...' : 'Refresh'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ar-content">
                        {reportFilterChips.length > 0 ? (
                            <div className="ar-filter-chips">
                                {reportFilterChips.map((chip) => (
                                    <span key={chip.key} className="ar-filter-chip">{chip.label}</span>
                                ))}
                                <button type="button" className="ar-filter-chips__clear" onClick={handleClearReportFilters}>Clear All</button>
                            </div>
                        ) : null}

                        {renderBody()}

                        <p className="etr-footnote">
                            Review and approve these tasks on <Link to="/tasks/ehr">EHR Tasks</Link>.
                        </p>
                    </div>

                    <ReportFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearReportFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={status === 'loading'}
                        hideTimeRange
                        sections={EHR_TASKS_REPORT_SECTIONS}
                        hideResponseTime
                    />

                    {/* Interval 1 (Today), as Maintenance stores: report_generator's date gate
                        needs one, and the report ignores it (plan section 5.2). */}
                    <ScheduleReportDrawer
                        open={scheduleDrawerOpen}
                        onClose={() => setScheduleDrawerOpen(false)}
                        onSave={handleScheduleSave}
                        saving={false}
                        reportTypeId={REPORT_TYPE_ID.EHR_TASKS}
                        rangeId={1}
                        rangeName="All pending tasks"
                        reportFilters={reportFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />
                </div>
            </div>
        </>
    );
};

export default EhrTasksReportPage;
